import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  getStatsSummary, getStatsDaily, getStatsTopViews,
  getStatsDemographics, getStatsHourly,
} from '../../../api/adminApi';
import { readDistribution, distributionTotal } from './distribution';

/* ── 시각 표시 ── */
function useClock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const fmt = () => new Date().toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
    setTime(fmt());
    const t = setInterval(() => setTime(fmt()), 60000);
    return () => clearInterval(t);
  }, []);
  return time;
}

/* ── 일별 가입 차트 ── */
/**
 * 막대마다 가입자 수와 날짜를 항상 보여준다.
 *
 * 전에는 마우스를 올려야 숫자가 나왔고 날짜는 14일 중 3개만 적혀 있어서,
 * 어느 날 몇 명인지 알려면 막대를 하나하나 짚어봐야 했다.
 */
const pct = (count, max) => Math.round((count / max) * 100);

function DailyChart({ data }) {
  if (!data?.length) return null;
  // 모두 0인 날도 축이 무너지지 않게 1을 바닥으로 둔다
  const max = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="ag-daily-chart">
      {data.map((d, i) => {
        const prevMonth = data[i - 1]?.date?.slice(0, 2);
        // 달이 바뀌는 칸과 첫 칸만 월을 함께 적는다. 14칸에 모두 적으면 글자가 겹친다
        const showMonth = i === 0 || d.date?.slice(0, 2) !== prevMonth;
        return (
          <div key={d.date ?? i} className="ag-daily-col" title={`${d.date} · ${d.count}명`}>
            {/* 숫자를 막대 바로 위에 띄운다 — 한 줄에 모아두면 어느 막대의 값인지 짚어야 한다 */}
            <div className="ag-daily-track">
              <div className="ag-daily-count" style={{ bottom: `calc(${pct(d.count, max)}% + 3px)` }}>
                {d.count}
              </div>
              <div className="ag-daily-bar" style={{ height: `${pct(d.count, max)}%` }} />
            </div>
            <div className="ag-daily-date">{showMonth ? d.date : d.date?.slice(3)}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ── 분포 바 ── */
function BarRow({ label, pct, count, color }) {
  return (
    <div className="ag-bar-row">
      <div className="ag-bar-label">{label}</div>
      <div className="ag-bar-track">
        <div className="ag-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      {/* 명 수는 서버가 비율만 줄 때 모른다 — 그 자리를 추정치로 채우지 않는다 */}
      {count != null && <div className="ag-bar-cnt">{count.toLocaleString()}명</div>}
      <div className="ag-bar-val">{pct}%</div>
    </div>
  );
}

/* ── 시간대 히트맵 ── */
function TimeGrid({ data }) {
  if (!data?.length) return null;
  const max   = Math.max(...data.map(d => d.count));
  const total = data.reduce((a, d) => a + d.count, 0);
  return (
    <div className="ag-time-grid">
      {data.map(({ hour, count }) => {
        const intensity = max > 0 ? count / max : 0;
        const bg = `rgba(15,15,15,${((intensity * 0.75) + 0.06).toFixed(2)})`;
        const light = intensity > 0.45;
        const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
        return (
          <div
            key={hour}
            className="ag-time-cell"
            style={{ background: bg, color: light ? '#fff' : 'var(--color-text-primary)' }}
            title={`${String(hour).padStart(2,'0')}~${String(hour+1).padStart(2,'0')}: ${count}명 (${pct}%)`}
          >
            <span className="ag-time-cell__val">{count}명</span>
            {String(hour).padStart(2,'0')}~{String(hour+1).padStart(2,'0')}
          </div>
        );
      })}
    </div>
  );
}

/* ── 주간/월간 비교 카드 ── */
function CompareCard({ title, current, prev, periodA, periodB }) {
  const delta = current - prev;
  const rate  = prev > 0 ? ((delta / prev) * 100).toFixed(1) : 0;
  const isUp  = delta >= 0;
  return (
    <div className="ag-card">
      <div className="ag-card-title">{title}</div>
      <div className="ag-compare-row">
        <div className="ag-compare-item">
          <div className="ag-compare-period">{periodA}</div>
          <div className="ag-compare-val">{current.toLocaleString()}명</div>
        </div>
        <div className="ag-compare-vs">vs</div>
        <div className="ag-compare-item">
          <div className="ag-compare-period">{periodB}</div>
          <div className="ag-compare-val">{prev.toLocaleString()}명</div>
        </div>
        <div className={`ag-compare-badge ag-compare-badge--${isUp ? 'up' : 'down'}`}>
          {isUp ? '▲' : '▼'} {isUp ? '+' : ''}{delta}명 ({isUp ? '+' : ''}{rate}%)
        </div>
      </div>
    </div>
  );
}

/* ── 로딩 스켈레톤 ── */
function Skeleton({ h = 24, w = '100%' }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: 4,
      background: 'linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%)',
      backgroundSize: '200% 100%',
      animation: 'ag-shimmer 1.4s infinite',
    }} />
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const time = useClock();

  const [summary,  setSummary]  = useState(null);
  const [daily,    setDaily]    = useState([]);
  const [topViews, setTopViews] = useState([]);
  const [demo,     setDemo]     = useState(null);   // { gender, age }
  const [hourly,   setHourly]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [s, d, t, dm, h] = await Promise.all([
          getStatsSummary(),
          getStatsDaily(14),
          getStatsTopViews(3),
          getStatsDemographics(),
          getStatsHourly(),
        ]);
        if (cancelled) return;

        // summary — 그대로 사용
        setSummary(s);

        // daily: { labels: ["05/16",...], data: [6,1,...] } → [{ date, count }]
        const dailyNorm = (d?.labels ?? []).map((label, i) => ({
          date: label,
          count: d?.data?.[i] ?? 0,
        }));
        setDaily(dailyNorm);

        // top-views: [{ id, name, views }] → viewCount 필드로 정규화
        setTopViews((t ?? []).map(item => ({ ...item, viewCount: item.views ?? item.viewCount ?? 0 })));

        // demographics: { gender: { male%, female% }, ageGroup: { teens, twenties, thirties, forties_plus } }
        // gender는 비율(%)로 오므로 그대로, ageGroup은 이미 집계된 값
        // 성별과 연령대는 분모가 다르다 — 연령대는 생년월일이 입력된 배우만 센다
        setDemo({
          gender: dm?.gender ?? { male: 0, female: 0 },
          genderTotal: dm?.genderTotal,
          ageGroup: dm?.ageGroup ?? {},
          ageGroupTotal: dm?.ageGroupTotal,
        });

        // hourly: { data: [숫자 24개] } → [{ hour, count }]
        const hourlyNorm = (h?.data ?? []).map((count, hour) => ({ hour, count }));
        setHourly(hourlyNorm);
      } catch (e) {
        if (!cancelled) setError('데이터를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // 서버가 명 수로 주면 그대로, 예전처럼 비율로 주면 비율만 쓴다
  const genderRows = readDistribution(demo?.gender, [
    { key: 'male',    label: '남성' },
    { key: 'female',  label: '여성' },
    { key: 'other',   label: '기타' },
    { key: 'unknown', label: '미입력' },
  ], demo?.genderTotal)
    // 기타·미입력은 0명이면 줄을 만들지 않는다. 늘 보이면 빈 줄만 늘어난다
    .filter((r, i) => i < 2 || r.count == null || r.count > 0);

  const ageRows = readDistribution(demo?.ageGroup, [
    { key: 'twenties',     label: '20대' },
    { key: 'thirties',     label: '30대' },
    { key: 'teens',        label: '10대' },
    { key: 'forties_plus', label: '40대+' },
  ], demo?.ageGroupTotal);

  const genderTotal = demo?.genderTotal ?? distributionTotal(genderRows);
  const ageTotal    = demo?.ageGroupTotal ?? distributionTotal(ageRows);

  const GENDER_COLORS = ['#0f0f0f', '#888888', '#bbbbbb', '#dddddd'];
  const AGE_COLORS    = ['#0f0f0f', '#555555', '#999999', '#cccccc'];

  return (
    <AdminLayout pageId="dashboard">
      <style>{`
        @keyframes ag-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {/* 탑바 */}
      <div className="admin-topbar">
        <h1>홈 대시보드</h1>
        <div className="admin-topbar__right">
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{time}</span>
          <span className="admin-badge"><i className="ti ti-user" aria-hidden="true" /> admin</span>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', fontSize: 13 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {/* 빠른 이동 */}
      <div className="ag-section-title">빠른 이동</div>
      <div className="ag-grid-quick">
        {[
          { icon: 'ti-users',              label: '배우 검색',   sub: '이름 / 전화번호', path: '/admin/talents' },
          { icon: 'ti-building',            label: '제작사 현황', sub: '총 가입 제작사',  path: '/admin/companies' },
          { icon: 'ti-chart-bar',           label: '가입 통계',   sub: '주간/월간 비교',  path: '/admin/dashboard' },
          { icon: 'ti-terminal',            label: '에러 로그',   sub: '실시간 모니터링', path: '/admin/dev' },
          { icon: 'ti-shield-exclamation',  label: '이상 보고',   sub: '알림 설정',       path: '/admin/dev?tab=alert' },
          { icon: 'ti-ban',                 label: 'IP 관리',     sub: '차단/허용 목록',  path: '/admin/dev?tab=blocked' },
        ].map(({ icon, label, sub, path }) => (
          <button key={label} className="ag-quick-btn" onClick={() => navigate(path)}>
            <i className={`ti ${icon}`} />
            <div className="ag-quick-btn__label">{label}</div>
            <div className="ag-quick-btn__sub">{sub}</div>
          </button>
        ))}
      </div>

      {/* 핵심 지표 4개 */}
      <div className="ag-grid-4">
        <div className="ag-metric-card">
          <div className="ag-metric-label"><i className="ti ti-users" style={{ fontSize: 13 }} /> 총 배우 수</div>
          <div className="ag-metric-val">
            {loading ? <Skeleton h={32} w={80} /> : (summary?.totalActors ?? '—').toLocaleString()}
          </div>
          <div className="ag-metric-delta ag-delta-up">
            {!loading && summary && `▲ ${summary.weeklyNewDelta} 이번 주`}
          </div>
        </div>
        <div className="ag-metric-card">
          <div className="ag-metric-label"><i className="ti ti-eye" style={{ fontSize: 13 }} /> 공개 프로필</div>
          <div className="ag-metric-val">
            {loading ? <Skeleton h={32} w={80} /> : (summary?.publicProfiles ?? '—').toLocaleString()}
          </div>
          <div className="ag-metric-delta ag-delta-neutral">
            {!loading && summary && `전체의 ${((summary.publicProfiles / summary.totalActors) * 100).toFixed(1)}%`}
          </div>
        </div>
        <div className="ag-metric-card">
          <div className="ag-metric-label"><i className="ti ti-building" style={{ fontSize: 13 }} /> 가입 제작사</div>
          <div className="ag-metric-val">
            {loading ? <Skeleton h={32} w={80} /> : (summary?.totalCompanies ?? '—').toLocaleString()}
          </div>
          <div className="ag-metric-delta ag-delta-neutral" />
        </div>
        <div className="ag-metric-card">
          <div className="ag-metric-label"><i className="ti ti-calendar" style={{ fontSize: 13 }} /> 이번 주 신규</div>
          <div className="ag-metric-val">
            {loading ? <Skeleton h={32} w={60} /> : (summary?.weeklyNew ?? '—')}
          </div>
          <div className={`ag-metric-delta ${(summary?.weeklyNewDelta ?? 0) >= 0 ? 'ag-delta-up' : 'ag-delta-down'}`}>
            {!loading && summary && `${summary.weeklyNewDelta >= 0 ? '▲' : '▼'} ${Math.abs(summary.weeklyNewDelta)} 전주 대비`}
          </div>
        </div>
      </div>

      {/* 일별 추이 + TOP3 */}
      <div className="ag-grid-3">
        <div className="ag-card">
          <div className="ag-card-title">일별 가입 추이 <span>최근 14일</span></div>
          {loading ? <Skeleton h={70} /> : <DailyChart data={daily} />}
        </div>
        <div className="ag-card">
          <div className="ag-card-title">조회수 TOP 3</div>
          {loading
            ? [1,2,3].map(i => <Skeleton key={i} h={24} style={{ marginBottom: 10 }} />)
            : topViews.map(({ id, name, viewCount }, i) => (
              <div key={id} className="ag-top3-row" onClick={() => navigate(`/admin/talents/${id}`)}>
                <div className={`ag-top3-rank ag-rank-${i + 1}`}>{i + 1}</div>
                <div className="ag-top3-name">{name}</div>
                <div className="ag-top3-cnt">조회 {(viewCount ?? 0).toLocaleString()}회</div>
              </div>
            ))
          }
        </div>
      </div>

      {/* 성별 / 연령대 */}
      <div className="ag-grid-2">
        <div className="ag-card">
          <div className="ag-card-title">
            성별 분포 {genderTotal != null && <span>총 {genderTotal.toLocaleString()}명</span>}
          </div>
          {loading ? <Skeleton h={60} /> : genderRows.map((r, i) => (
            <BarRow key={r.label} {...r} color={GENDER_COLORS[i]} />
          ))}
        </div>
        <div className="ag-card">
          <div className="ag-card-title">
            연령대 분포 {ageTotal != null && <span>생년월일 입력 {ageTotal.toLocaleString()}명</span>}
          </div>
          {loading ? <Skeleton h={80} /> : ageRows.map((r, i) => (
            <BarRow key={r.label} {...r} color={AGE_COLORS[i]} />
          ))}
        </div>
      </div>

      {/* 시간대 히트맵 */}
      <div className="ag-card">
        <div className="ag-card-title">가입 시간대 분포 <span>전체 기간 기준</span></div>
        {loading ? <Skeleton h={80} /> : <TimeGrid data={hourly} />}
      </div>

      {/* 주간 / 월간 비교 */}
      {!loading && summary && (
        <div className="ag-grid-2" style={{ marginTop: 12 }}>
          <CompareCard
            title="주간 신규 비교"
            current={summary.weeklyNew}
            prev={summary.weeklyNew - summary.weeklyNewDelta}
            periodA="이번 주" periodB="지난 주"
          />
          <CompareCard
            title="월간 신규 비교"
            current={summary.monthlyNew}
            prev={summary.monthlyNew - summary.monthlyNewDelta}
            periodA="이번 달" periodB="지난 달"
          />
        </div>
      )}
    </AdminLayout>
  );
}
